/**
 * HC Observations Page — Shared observation board for all HC agents
 * Shows healer observations, PM2 alerts, and healing actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Tag, Card, Row, Col, Statistic, Select, Space, Button,
  Typography, Empty, Alert, Tooltip, Badge, Input
} from 'antd';
import {
  EyeOutlined, ReloadOutlined, WarningOutlined,
  CheckCircleOutlined, CloseCircleOutlined, SearchOutlined,
  ThunderboltOutlined, RobotOutlined, HeartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { observationApi } from '../api/hcApi';

const { Title, Text } = Typography;
const { Option } = Select;

interface ObservationRecord {
  id: string;
  timestamp: string;
  module: string;
  policyId: string;
  policyName: string;
  alertType: string;
  severity: string;
  source: string;
  mode: string;
  details?: string;
  process?: string;
  wouldSucceed?: boolean;
  success?: boolean;
  successRate?: string;
  observationCount?: number;
  wouldHaveExecuted?: string[];
}

const severityColors: Record<string, string> = {
  L1: 'blue',
  L2: 'orange',
  L3: 'red',
  critical: 'red',
  warning: 'orange',
  info: 'blue',
};

const modeIcons: Record<string, React.ReactNode> = {
  observation: <EyeOutlined />,
  execution: <ThunderboltOutlined />,
  detection: <WarningOutlined />,
};

const Observations: React.FC = () => {
  const [observations, setObservations] = useState<ObservationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string | undefined>();
  const [moduleFilter, setModuleFilter] = useState<string | undefined>();
  const [searchText, setSearchText] = useState('');
  const [stats, setStats] = useState({ total: 0, observations: 0, executions: 0, detections: 0 });

  const fetchObservations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { limit: 100 };
      if (severityFilter) params.severity = severityFilter;
      if (moduleFilter) params.source = moduleFilter;

      const response = await observationApi.list(params);
      const data = response?.data || [];

      // Map to consistent format (API returns mixed formats from log + DB)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapped = (Array.isArray(data) ? data : []).map((obs: any) => ({
        id: (obs.id as string) || `obs-${Math.random().toString(36).substr(2, 8)}`,
        timestamp: (obs.timestamp as string) || new Date().toISOString(),
        module: (obs.module as string) || (obs.source as string) || 'unknown',
        policyId: (obs.policyId as string) || (obs.policy_id as string) || '-',
        policyName: (obs.policyName as string) || (obs.policy_name as string) || (obs.policyId as string) || '-',
        alertType: (obs.alertType as string) || (obs.alert_type as string) || (obs.type as string) || '-',
        severity: (obs.severity as string) || 'L1',
        source: (obs.source as string) || (obs.module as string) || 'unknown',
        mode: (obs.mode as string) || 'observation',
        details: (obs.details as string) || (obs.description as string) || '',
        process: (obs.process as string) || '',
        wouldSucceed: obs.wouldSucceed as boolean,
        success: obs.success as boolean,
        successRate: (obs.successRate as string) || '',
        observationCount: obs.observationCount as number,
        wouldHaveExecuted: obs.wouldHaveExecuted as string[],
      }));

      setObservations(mapped);
      setStats({
        total: mapped.length,
        observations: mapped.filter((o: ObservationRecord) => o.mode === 'observation').length,
        executions: mapped.filter((o: ObservationRecord) => o.mode === 'execution').length,
        detections: mapped.filter((o: ObservationRecord) => o.mode === 'detection').length,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch observations';
      setError(message);
      setObservations([]);
    } finally {
      setLoading(false);
    }
  }, [severityFilter, moduleFilter]);

  useEffect(() => {
    fetchObservations();
    const interval = setInterval(fetchObservations, 30000); // Auto-refresh 30s
    return () => clearInterval(interval);
  }, [fetchObservations]);

  const filteredObs = searchText
    ? observations.filter(o =>
        o.alertType.toLowerCase().includes(searchText.toLowerCase()) ||
        o.module.toLowerCase().includes(searchText.toLowerCase()) ||
        o.policyName.toLowerCase().includes(searchText.toLowerCase()) ||
        (o.details || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (o.process || '').toLowerCase().includes(searchText.toLowerCase())
      )
    : observations;

  const columns = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (ts: string) => (
        <Tooltip title={ts}>
          <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(ts).format('MMM D HH:mm:ss')}</Text>
        </Tooltip>
      ),
      sorter: (a: ObservationRecord, b: ObservationRecord) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      defaultSortOrder: 'ascend' as const,
    },
    {
      title: 'Mode',
      dataIndex: 'mode',
      key: 'mode',
      width: 110,
      render: (mode: string) => {
        const colors: Record<string, string> = {
          observation: 'cyan', execution: 'green', detection: 'volcano'
        };
        return (
          <Tag icon={modeIcons[mode]} color={colors[mode] || 'default'}>
            {mode.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 90,
      render: (sev: string) => (
        <Tag color={severityColors[sev] || 'default'}>{sev}</Tag>
      ),
    },
    {
      title: 'Module',
      dataIndex: 'module',
      key: 'module',
      width: 140,
      render: (mod: string) => (
        <Tag icon={<RobotOutlined />} color="geekblue">{mod}</Tag>
      ),
    },
    {
      title: 'Alert / Policy',
      key: 'alert',
      render: (_: unknown, record: ObservationRecord) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{record.alertType}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{record.policyName}</Text>
          {record.process && (
            <Text code style={{ fontSize: 11 }}>{record.process}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Result',
      key: 'result',
      width: 120,
      render: (_: unknown, record: ObservationRecord) => {
        if (record.mode === 'observation') {
          return record.wouldSucceed
            ? <Tag icon={<CheckCircleOutlined />} color="success">Would Heal</Tag>
            : <Tag icon={<CloseCircleOutlined />} color="warning">Would Fail</Tag>;
        }
        if (record.mode === 'execution') {
          return record.success
            ? <Tag icon={<CheckCircleOutlined />} color="success">Healed</Tag>
            : <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>;
        }
        return <Tag icon={<WarningOutlined />} color="volcano">Detected</Tag>;
      },
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      ellipsis: true,
      render: (details: string, record: ObservationRecord) => (
        <Space direction="vertical" size={0}>
          {details && <Text style={{ fontSize: 12 }}>{details}</Text>}
          {record.successRate && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Success rate: {record.successRate}
            </Text>
          )}
        </Space>
      ),
    },
  ];

  const uniqueModules = [...new Set(observations.map(o => o.module))].sort();

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>
          <HeartOutlined style={{ marginRight: 8 }} />
          HC Observation Board
        </Title>
        <Button icon={<ReloadOutlined />} onClick={fetchObservations} loading={loading}>
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Total Events" value={stats.total} prefix={<EyeOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Observations"
              value={stats.observations}
              prefix={<EyeOutlined style={{ color: '#13c2c2' }} />}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Executions"
              value={stats.executions}
              prefix={<ThunderboltOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Detections"
              value={stats.detections}
              prefix={<WarningOutlined style={{ color: '#fa541c' }} />}
              valueStyle={{ color: '#fa541c' }}
            />
          </Card>
        </Col>
      </Row>

      {error && (
        <Alert
          message="Failed to load observations"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Search alerts, modules, processes..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          <Select
            placeholder="Severity"
            value={severityFilter}
            onChange={setSeverityFilter}
            allowClear
            style={{ width: 120 }}
          >
            <Option value="L1">L1 - Info</Option>
            <Option value="L2">L2 - Warning</Option>
            <Option value="L3">L3 - Critical</Option>
          </Select>
          <Select
            placeholder="Module"
            value={moduleFilter}
            onChange={setModuleFilter}
            allowClear
            style={{ width: 160 }}
          >
            {uniqueModules.map(m => (
              <Option key={m} value={m}>{m}</Option>
            ))}
          </Select>
          <Badge count={filteredObs.length} overflowCount={999} style={{ backgroundColor: '#1890ff' }}>
            <Text type="secondary">results</Text>
          </Badge>
        </Space>
      </Card>

      {/* Observations Table */}
      <Table
        columns={columns}
        dataSource={filteredObs}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} observations` }}
        locale={{ emptyText: <Empty description="No observations yet. HC agents will populate this board as they detect and respond to events." /> }}
        scroll={{ x: 900 }}
      />
    </div>
  );
};

export default Observations;
